import type { PassportDetail } from "../api/types";

const SIGN_IN_KEYS = ["signin_1", "signin_2", "signin_3", "signin_4"];
const TIME_OUT_KEYS = [
  "timeout_1",
  "timeout_2",
  "timeout_3",
  "timeout_4",
  "timeout_5",
  "timeout_6",
  "timeout_7",
  "timeout_8",
];

function checklistDone(passport: Pick<PassportDetail, "verifications">, keys: string[]) {
  const stamps = new Set(
    (passport.verifications || [])
      .filter((v) => v.matched && (v.type.startsWith("aho_") || keys.includes(String(v.payload?.key || ""))))
      .map((v) => (v.type.startsWith("aho_") ? v.type.slice(4) : String(v.payload?.key || ""))),
  );
  return keys.every((k) => stamps.has(k));
}

function hasType(passport: Pick<PassportDetail, "verifications">, type: string) {
  return (passport.verifications || []).some((v) => v.type === type && v.matched);
}

export function signInComplete(passport: Pick<PassportDetail, "verifications">) {
  return checklistDone(passport, SIGN_IN_KEYS);
}

export function timeOutComplete(passport: Pick<PassportDetail, "verifications">) {
  return checklistDone(passport, TIME_OUT_KEYS);
}

export function orRoomWorkStarted(passport: Pick<PassportDetail, "verifications">) {
  return (
    hasType(passport, "or_face") ||
    hasType(passport, "or_wristband") ||
    hasType(passport, "surgeon_confirm") ||
    hasType(passport, "anesthesia_confirm") ||
    hasType(passport, "scrub_nurse_confirm") ||
    hasType(passport, "circulator_confirm") ||
    signInComplete(passport) ||
    timeOutComplete(passport)
  );
}

/** Same titles as iOS PassportListStatus. */
export function statusLabel(passport: Pick<PassportDetail, "status" | "preop_started_at" | "verifications">): string {
  switch (passport.status) {
    case "draft":
      return "Started";
    case "locked":
      return passport.preop_started_at ? "In Pre-Op" : "Ready for Pre-Op";
    case "preop_verified":
      if (signInComplete(passport)) return "In Time Out";
      if (orRoomWorkStarted(passport)) return "Room Sign In";
      return "Waiting for OR";
    case "incision_verified":
      return "Incision";
    case "cancelled":
      return "CANCELLED";
    default:
      return passport.status.toUpperCase();
  }
}

export function statusClass(status: string) {
  if (status === "incision_verified") return "done";
  if (status === "preop_verified") return "or";
  if (status === "locked") return "preop";
  if (status === "cancelled") return "hold";
  if (status === "draft") return "hold";
  return "";
}

export function verificationLabel(type: string) {
  if (type === "patient_face" || type === "or_face") return "Face ID";
  if (type === "wristband_mrn" || type === "or_wristband") return "Wristband";
  if (type === "dual_preop") return "Unlock Surgical Passport";
  if (type === "incision_verified") return "Incision";
  if (type === "surgeon_confirm") return "Surgeon agrees";
  if (type === "anesthesia_confirm") return "Anesthesia provider agrees";
  if (type === "scrub_nurse_confirm") return "Scrub nurse agrees";
  if (type === "circulator_confirm") return "Circulator agrees";
  if (type.startsWith("aho_signin")) return "Room Sign In";
  if (type.startsWith("aho_timeout")) return "Time Out";
  return type;
}

export type LifecycleStep = {
  id: string;
  title: string;
  at?: string | null;
  state: "done" | "now" | "pending" | "alert";
};

function firstVerificationAt(passport: PassportDetail, match: (type: string) => boolean): string | null {
  const hit = (passport.verifications || []).find((v) => v.matched && match(v.type));
  return hit?.created_at || null;
}

export function lifecycleSteps(passport: PassportDetail): LifecycleStep[] {
  const current = statusLabel(passport);
  const cancelled = passport.status === "cancelled";
  const signInAt = firstVerificationAt(
    passport,
    (t) => t === "or_face" || t === "or_wristband" || t.startsWith("aho_signin"),
  );
  const timeOutAt = firstVerificationAt(passport, (t) => t.startsWith("aho_timeout"));
  const steps: Array<{ id: string; title: string; at?: string | null }> = [
    { id: "started", title: "Started", at: passport.encounter_at },
    { id: "ready", title: "Ready for Pre-Op", at: passport.locked_at },
    { id: "preop", title: "In Pre-Op", at: passport.preop_started_at },
    { id: "waiting", title: "Waiting for OR", at: passport.preop_verified_at },
    { id: "signin", title: "Room Sign In", at: signInAt },
    { id: "timeout", title: "In Time Out", at: timeOutAt },
    { id: "incision", title: "Incision", at: passport.incision_verified_at },
    { id: "archive", title: "Archive", at: passport.case_completed_at },
  ];
  if (cancelled) {
    steps.push({ id: "cancelled", title: "CANCELLED", at: passport.cancelled_at });
  }

  return steps.map((step) => {
    if (cancelled && step.id === "cancelled") return { ...step, state: "alert" as const };
    if (step.at) return { ...step, state: "done" as const };
    if (step.title === current) return { ...step, state: "now" as const };
    return { ...step, state: "pending" as const };
  });
}
